import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { collection, onSnapshot, query, where, QueryConstraint } from "firebase/firestore";
import { startOfDay, endOfDay } from "date-fns";
import { db } from "@/firebase";
import useOrganizations from "@/features/organizations/useOrganizations";
import { Order, OrderStatus } from "@/types";
import { Stack } from "@mui/material";
import OrderDetails from "./components/OrderDetailsDrawer";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { fetchUsers } from "@/store/usersSlice";
import OrderStagesView from "./OrdersStages.view";
import { ActiveView } from "./OrdersPage";
import { useIncomingOrders } from "./IncomingOrdersProvider";

const IncomingOrders = () => {
  const dispatch = useAppDispatch();
  const { orderId } = useParams<{ orderId: string }>();
  const { currentOrganization } = useOrganizations();
  const activeBranch = useAppSelector((state) => state.ui.activeBranch);
  const [activeOrders, setActiveOrders] = useState<Order[]>([]);

  const activeView = useMemo(() => {
    const qs = new URLSearchParams(window.location.search);
    return qs.get("view") || ActiveView.Stages;
  }, []);

  const { orders: reviewingOrders } = useIncomingOrders();

  useEffect(() => {
    if (!currentOrganization?.id) return;

    const ordersRef = collection(db, `organizations/${currentOrganization.id}/orders`);
    const start = startOfDay(new Date());
    const end = endOfDay(new Date());

    const conditions: QueryConstraint[] = [
      where("orderedAt", ">=", start.getTime()),
      where("orderedAt", "<=", end.getTime()),
      where("status", "in", [
        OrderStatus.PENDING_PAYMENT,
        OrderStatus.PREPARING,
        OrderStatus.SENDING,
        OrderStatus.WAITING,
      ])
    ];

    const ordersQuery = query(ordersRef, ...conditions);

    const unsubscribe = onSnapshot(ordersQuery, (snapshot) => {
      const data: Order[] = [];
      snapshot.forEach((doc) => {
        const order = { ...doc.data(), id: doc.id } as Order;
        const branchMatches = !activeBranch || order.branch?.id === activeBranch || (order as any).branchId === activeBranch;
        if (branchMatches) {
          data.push(order);
        }
      });
      setActiveOrders(data);
    });

    return () => unsubscribe();
  }, [currentOrganization?.id, activeBranch]);

  const allOrders = useMemo(() => [...reviewingOrders, ...activeOrders], [reviewingOrders, activeOrders]);

  const activeOrder = useMemo(() => {
    if (!orderId) return null;
    return allOrders.find((o) => o.id === orderId) || null;
  }, [orderId, allOrders]);

  useEffect(() => {
    if (!activeOrder?.createdBy) return;
    dispatch(fetchUsers([activeOrder.createdBy]));
  }, [activeOrder?.createdBy, dispatch]);

  return (
    <Stack spacing={2} height="100%" pb={3}>
      {activeView === ActiveView.Stages && (
        <OrderStagesView activeOrders={activeOrders} />
      )}

      <OrderDetails order={activeOrder} />
    </Stack>
  );
};

export default IncomingOrders;